import { Image } from "@chakra-ui/react";
import { ReactNode } from "react";
import { FormattedMessage } from "react-intl";

import { Status } from "@nens/rana/api-v1";
import { RanaLoading } from "@nens/ranalib";

import { HeaderBox } from "@/components/header-box";
import { FileDataTypeEnum } from "@/lib/rana-file-data-type";
import { useFileDescriptorQuery } from "@/queries/query-file-descriptors";
import { useFileDownloadQuery } from "@/queries/query-file-download";
import { ProjectFile } from "@/types/api-model-simple-names";
import { getNameFromFileId } from "@/utils/get-name-from-file-id";
import { isTypedDescriptor } from "@/utils/is-descriptor-type";

import { ProjectFilePreviewEdit } from "./project-file-preview-edit";
import { ProjectFilePreviewOGC } from "./project-file-preview-ogc";
import { ProjectFilePreviewRaster } from "./project-file-preview-raster";
import { ProjectFilePreviewScenario } from "./project-file-preview-scenario";
import { ProjectFilePreviewSchematisation } from "./project-file-preview-schematisation";
import { ProjectFilePreviewStatus } from "./project-file-preview-status";
import { ProjectFilePreviewVector } from "./project-file-preview-vector";
import { ProjectFileStatusWithSpinner } from "./project-file-status";

const Loading = () => <RanaLoading size="xl" color="brand.clear_creek" />;

const PreviewRequiresDownload = ({
  children,
  file,
}: {
  file: ProjectFile;
  children: (url: string) => ReactNode;
}) => {
  const { data, isLoading, isSuccess } = useFileDownloadQuery(file);

  if (isLoading) return <Loading />;

  if (!isSuccess)
    return (
      <FormattedMessage
        id="filepreview.could_not_download"
        defaultMessage="Could not get download url"
      />
    );

  return children(data);
};

export interface ProjectFilePreviewProps {
  file: ProjectFile;
  fileRef: string | undefined;
  name?: JSX.Element | string;
}

// Wrapper element needed so the file edit can have its own `HeaderBox` implementation
export function ProjectFilePreview(props: ProjectFilePreviewProps) {
  const { file, name } = props;
  const { dataType } = file;

  if (dataType === FileDataTypeEnum.Code || dataType === FileDataTypeEnum.Text)
    return <ProjectFilePreviewEdit name={name} file={file} />;

  return (
    <HeaderBox
      name={name ?? <FormattedMessage id="filepreview.preview" defaultMessage="Preview" />}
    >
      <ProjectFilePreviewInner {...props} />
    </HeaderBox>
  );
}

// Here comes any implemnation that doesnt require any knowledge of the file descriptor like an image
export function ProjectFilePreviewInner(props: ProjectFilePreviewProps) {
  const { file } = props;
  const { dataType } = file;

  if (!dataType) return <Loading />;

  if (dataType === FileDataTypeEnum.Image)
    return (
      <PreviewRequiresDownload file={file}>
        {(url) => <Image maxH="full" maxW="full" src={url} />}
      </PreviewRequiresDownload>
    );

  // OGC 3D files cannot be shown online (yet)
  if (file.dataType === FileDataTypeEnum.Ogc) {
    return <ProjectFilePreviewOGC />;
  }

  if (file.dataType === FileDataTypeEnum.Shapefile) {
    return (
      <ProjectFileStatusWithSpinner>
        <FormattedMessage
          id="shapefile.converting"
          defaultMessage="Shapefile is being converted to a previewable format."
        />
      </ProjectFileStatusWithSpinner>
    );
  }

  return <ProjectFilePreviewInnerInner {...props} />;
}

// All of these implementations require knowledge of the descriptor object.
const ProjectFilePreviewInnerInner = ({ file, fileRef }: ProjectFilePreviewProps) => {
  const { isPending, isSuccess, data: descriptor } = useFileDescriptorQuery(file.descriptorId);

  if (isPending) return <Loading />;

  if (!isSuccess) return <ProjectFilePreviewStatus fileRef={fileRef} file={file} isError />;

  if (descriptor.status && descriptor.status.id !== Status.Completed) {
    return <ProjectFilePreviewStatus fileRef={fileRef} file={file} status={descriptor.status} />;
  }

  if (isTypedDescriptor(descriptor, "raster")) {
    return <ProjectFilePreviewRaster descriptor={descriptor} title={getNameFromFileId(file.id)} />;
  }

  if (isTypedDescriptor(descriptor, "vector")) {
    return <ProjectFilePreviewVector descriptor={descriptor} title={getNameFromFileId(file.id)} />;
  }

  if (isTypedDescriptor(descriptor, "scenario")) {
    return (
      <ProjectFilePreviewScenario descriptor={descriptor} title={getNameFromFileId(file.id)} />
    );
  }

  if (isTypedDescriptor(descriptor, "threedi_schematisation")) {
    return (
      <ProjectFilePreviewSchematisation
        file={file}
        descriptor={descriptor}
        title={getNameFromFileId(file.id)}
      />
    );
  }

  return <ProjectFilePreviewStatus fileRef={fileRef} file={file} />;
};
